import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

interface Feriado {
  id: number;
  dtFeriado: string;
  sDescricao: string;
  iTipo: number;
  sUf: string;
  sMunicipio: string;
  ibFacultativo: boolean;
  ibRecorrenteAnual: boolean;
  ibAtivo: boolean;
  sObs: string;
  tipoTexto: string;
}

const anoAtual = new Date().getFullYear();

const VAZIO = {
  id: 0,
  DtFeriado: `${anoAtual}-01-01`,
  SDescricao: "",
  ITipo: 1,
  SUf: "",
  SMunicipio: "",
  IbFacultativo: false,
  IbRecorrenteAnual: true,
  IbAtivo: true,
  SObs: "",
};

export default function FeriadosPage() {
  const [ano, setAno] = useState(anoAtual);
  const [itens, setItens] = useState<Feriado[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO);
  const [excluirId, setExcluirId] = useState<number | null>(null);

  const carregar = () => {
    setCarregando(true);
    apiClient
      .get<Feriado[]>("/api/rh/ponto/feriados", { params: { ano } })
      .then((r) => setItens(r.data))
      .catch((e) => toast.error(e.message ?? "Falha ao carregar."))
      .finally(() => setCarregando(false));
  };

  useEffect(carregar, [ano]);

  const novo = () => { setForm({ ...VAZIO, DtFeriado: `${ano}-01-01` }); setDialog(true); };
  const editar = (f: Feriado) => {
    setForm({
      id: f.id, DtFeriado: f.dtFeriado.slice(0, 10), SDescricao: f.sDescricao, ITipo: f.iTipo,
      SUf: f.sUf, SMunicipio: f.sMunicipio, IbFacultativo: f.ibFacultativo,
      IbRecorrenteAnual: f.ibRecorrenteAnual, IbAtivo: f.ibAtivo, SObs: f.sObs,
    });
    setDialog(true);
  };

  const salvar = async () => {
    try {
      await apiClient.post("/api/rh/ponto/feriados", form);
      toast.success("Feriado salvo.");
      setDialog(false);
      carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const excluir = async () => {
    if (excluirId == null) return;
    try {
      await apiClient.delete(`/api/rh/ponto/feriados/${excluirId}`);
      toast.success("Feriado excluído.");
      setExcluirId(null);
      carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao excluir."); }
  };

  const gerarNacionais = async () => {
    try {
      const r = await apiClient.post(`/api/rh/ponto/feriados/gerar-nacionais`, null, { params: { ano } });
      toast.success(r.data?.mensagem ?? "Feriados gerados.");
      carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao gerar."); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Feriados</h1>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Ano</Label>
          <Input type="number" className="w-24" value={ano} onChange={(e) => setAno(Number(e.target.value) || anoAtual)} />
          <Button variant="outline" onClick={gerarNacionais}><CalendarPlus className="h-4 w-4 mr-2" />Gerar nacionais</Button>
          <Button onClick={novo}><Plus className="h-4 w-4 mr-2" />Novo</Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Abrangência</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {carregando ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Nenhum feriado.</TableCell></TableRow>
            ) : itens.map((f) => (
              <TableRow key={f.id} className={!f.ibAtivo ? "opacity-50" : undefined}>
                <TableCell className="whitespace-nowrap">
                  {f.dtFeriado.slice(8, 10)}/{f.dtFeriado.slice(5, 7)}
                  {f.ibRecorrenteAnual && <Badge variant="outline" className="ml-2">anual</Badge>}
                </TableCell>
                <TableCell>{f.sDescricao}</TableCell>
                <TableCell>{f.ibFacultativo ? <Badge variant="secondary">Facultativo</Badge> : f.tipoTexto}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{[f.sUf, f.sMunicipio].filter(Boolean).join(" · ") || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => editar(f)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setExcluirId(f.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Editar feriado" : "Novo feriado"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Data</Label>
              <Input type="date" value={form.DtFeriado} onChange={(e) => setForm({ ...form, DtFeriado: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={String(form.ITipo)} onValueChange={(v) => setForm({ ...form, ITipo: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Nacional</SelectItem>
                  <SelectItem value="2">Estadual</SelectItem>
                  <SelectItem value="3">Municipal</SelectItem>
                  <SelectItem value="4">Facultativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-2">
              <Label>Descrição</Label>
              <Input value={form.SDescricao} onChange={(e) => setForm({ ...form, SDescricao: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>UF (estadual)</Label>
              <Input maxLength={2} value={form.SUf} onChange={(e) => setForm({ ...form, SUf: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1">
              <Label>Município (municipal)</Label>
              <Input value={form.SMunicipio} onChange={(e) => setForm({ ...form, SMunicipio: e.target.value })} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.IbFacultativo} onCheckedChange={(v) => setForm({ ...form, IbFacultativo: v })} />
              <Label>Ponto facultativo</Label>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.IbRecorrenteAnual} onCheckedChange={(v) => setForm({ ...form, IbRecorrenteAnual: v })} />
              <Label>Recorrente anual</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.IbAtivo} onCheckedChange={(v) => setForm({ ...form, IbAtivo: v })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={excluirId != null} onOpenChange={(o) => !o && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir feriado?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={excluir}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
